import { useState } from "react";
import { 
  BarChart3, 
  Shield, 
  Zap, 
  Users, 
  Workflow,
  Globe,
  CheckCircle2,
  Sparkles
} from "lucide-react";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { ScrollReveal } from "@/components/animations/ScrollAnimations";
import webSaasInterface from "@/assets/web_saas_interface.png";

const platformFeatures = [
  {
    id: "dashboard",
    icon: BarChart3,
    title: "Real-time Dashboard",
    description: "Monitor all stations, energy, and revenue from a single view.",
    details: ["Live station status", "Energy analytics", "Revenue tracking", "Custom KPIs", "Multi-site management"],
    color: "primary"
  },
  {
    id: "security",
    icon: Shield,
    title: "Enterprise Security",
    description: "FedRAMP authorized with SOC 2 compliance.",
    details: ["FedRAMP authorized", "SOC 2 Type II", "End-to-end encryption", "Role-based access", "Audit logging"],
    color: "accent"
  },
  {
    id: "load",
    icon: Zap,
    title: "Smart Load Management",
    description: "Dynamic power distribution and peak shaving.",
    details: ["Dynamic balancing", "Peak shaving", "Time-of-use optimization", "Circuit protection", "Power scheduling"],
    color: "primary"
  },
  {
    id: "users",
    icon: Users,
    title: "Driver Management",
    description: "Complete driver lifecycle with groups and billing.",
    details: ["Registration portal", "Group access control", "RFID management", "Activity reports", "Custom pricing"],
    color: "accent"
  },
  {
    id: "integrations",
    icon: Workflow,
    title: "API & Integrations",
    description: "Connect with ERP, billing, and utility systems.",
    details: ["RESTful API", "Webhooks", "ERP integrations", "Telematics", "Utility exchange"],
    color: "primary"
  },
  {
    id: "whitelabel",
    icon: Globe,
    title: "White-Label Ready",
    description: "Fully customizable branding across all touchpoints.",
    details: ["Custom domain", "Brand theming", "Logo customization", "Email templates", "Branded apps"],
    color: "accent"
  }
];

export function SaaSPlatformNew() {
  const [activeFeature, setActiveFeature] = useState(platformFeatures[0]);

  return (
    <section id="platform" className="py-28 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-subtle" />
      <div className="absolute top-0 left-0 w-full section-divider" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
          <ScrollReveal>
            <motion.div 
              className="inline-flex items-center gap-2 badge-primary mb-6"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Sparkles className="h-3.5 w-3.5" />
              Core Platform
            </motion.div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
              SaaS <span className="text-gradient">Platform</span>
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={0.2}>
            <p className="text-muted-foreground lg:max-w-md text-lg font-light leading-relaxed">
              Hardware-agnostic, OCPP-compliant platform for complete control over your EV charging network.
            </p>
          </ScrollReveal>
        </div>

        {/* Platform Preview Image */}
        <ScrollReveal className="mb-20">
          <motion.div 
            className="glass-card p-6 md:p-10 rounded-3xl"
            whileHover={{ scale: 1.005 }}
            transition={{ duration: 0.3 }}
          >
            <img 
              src={webSaasInterface} 
              alt="EVGateway SaaS platform dashboard showing charging statistics, station management, and analytics" 
              className="w-full object-contain rounded-2xl"
            />
          </motion.div>
        </ScrollReveal>

        {/* Bento Layout */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Feature Selector - Left Column */}
          <div className="lg:col-span-1 space-y-3">
            {platformFeatures.map((feature) => (
              <motion.button
                key={feature.id}
                onClick={() => setActiveFeature(feature)}
                whileHover={{ x: 4 }}
                initial={{ opacity: 1 }}
                className={cn(
                  "w-full text-left p-5 rounded-2xl transition-all duration-300 border",
                  activeFeature.id === feature.id
                    ? `border-${feature.color}/40 glass-card`
                    : "border-border/30 hover:bg-card/40 bg-card/20"
                )}
              >
                <div className="flex items-center gap-4">
                  <div className={cn(
                    "w-12 h-12 rounded-xl flex items-center justify-center border transition-all",
                    activeFeature.id === feature.id 
                      ? `bg-${feature.color}/15 border-${feature.color}/30` 
                      : "bg-muted/50 border-border/30"
                  )}>
                    <feature.icon className={cn(
                      "h-5 w-5 transition-colors",
                      activeFeature.id === feature.id ? `text-${feature.color}` : "text-muted-foreground"
                    )} />
                  </div>
                  <div>
                    <h3 className={cn(
                      "font-semibold transition-colors",
                      activeFeature.id === feature.id ? "text-foreground" : "text-foreground/80"
                    )}>
                      {feature.title}
                    </h3>
                  </div>
                </div>
              </motion.button>
            ))}
          </div>

          {/* Feature Details - Center & Right */}
          <motion.div 
            key={activeFeature.id}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:col-span-2 glass-card rounded-3xl p-8 md:p-10"
          >
            <div className={`w-14 h-14 rounded-2xl bg-${activeFeature.color}/10 border border-${activeFeature.color}/20 flex items-center justify-center mb-6`}>
              <activeFeature.icon className={`h-7 w-7 text-${activeFeature.color}`} />
            </div>
            <h3 className="text-2xl md:text-3xl font-bold mb-3 tracking-tight text-white">{activeFeature.title}</h3>
            <p className="text-white/60 text-lg mb-8 font-light">{activeFeature.description}</p>
            
            <div className="grid sm:grid-cols-2 gap-4">
              {activeFeature.details.map((detail, idx) => (
                  <motion.div 
                    key={idx} 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="flex items-center gap-3 bg-white/5 rounded-xl p-4 border border-white/10"
                  >
                    <CheckCircle2 className={`h-5 w-5 text-${activeFeature.color} flex-shrink-0`} />
                    <span className="text-sm font-medium text-white">{detail}</span>
                  </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Compliance Bar */}
        <ScrollReveal delay={0.3}>
          <div className="mt-16 glass-card p-8 rounded-3xl">
            <div className="flex flex-wrap items-center justify-center gap-8">
              {["OCPP 1.6", "OCPP 2.0.1", "FedRAMP", "SOC 2", "ISO 27001"].map((badge, idx) => (
                <motion.div 
                  key={idx} 
                  className="flex items-center gap-2"
                  whileHover={{ scale: 1.05 }}
                >
                  <CheckCircle2 className="h-5 w-5 text-primary" />
                  <span className="text-sm font-semibold text-white">{badge}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </ScrollReveal>
      </div>
      
      <div className="absolute bottom-0 left-0 w-full section-divider" />
    </section>
  );
}